// Block-based email templates.
// A template is a 600px table; every block is one or more table rows (<tr>...</tr>) with
// {{placeholders}} that are filled with the brand settings and the creative content.

export const BLOCKS_STRUCTURE_VERSION = 2;

export const isBlocksStructure = (structure) =>
  !!structure && !Array.isArray(structure) && Array.isArray(structure.blocks);

export const createBlocksStructure = (blocks = []) => ({
  version: BLOCKS_STRUCTURE_VERSION,
  blocks
});

export const BRAND_PLACEHOLDERS = [
  { value: '{{brand.name}}', label: 'Brand name' },
  { value: '{{brand.logo}}', label: 'Logo URL' },
  { value: '{{brand.logoLink}}', label: 'Logo link' },
  { value: '{{brand.backgroundColor}}', label: 'Background color' },
  { value: '{{brand.frameColor}}', label: 'Frame color' },
  { value: '{{brand.textColor}}', label: 'Text color (auto)' },
  { value: '{{brand.mutedTextColor}}', label: 'Muted text color (auto)' },
  { value: '{{brand.buttonColor}}', label: 'Button color' },
  { value: '{{brand.buttonTextColor}}', label: 'Button text color' },
  { value: '{{brand.borderRadius}}', label: 'Button radius' },
  { value: '{{brand.viewInBrowserURL}}', label: 'View in browser URL' },
  { value: '{{brand.unsubscribeURL}}', label: 'Unsubscribe URL' },
  { value: '{{brand.disclaimer}}', label: 'Marketing disclaimer' },
  { value: '{{brand.smallDisclaimer}}', label: 'Small disclaimer' }
];

export const CONTENT_PLACEHOLDERS = [
  { value: '{{headline}}', label: 'Headline' },
  { value: '{{body}}', label: 'Body' },
  { value: '{{bodyImage}}', label: 'Image URL' },
  { value: '{{bodyImageLink}}', label: 'Image link' },
  { value: '{{bodyBtnName}}', label: 'Button text' },
  { value: '{{bodyBtnLink}}', label: 'Button link' },
  { value: '{{additionalText}}', label: 'Additional text' },
  { value: '{{additionalTextUnderBTN}}', label: 'Text under button' },
  { value: '{{preHeader}}', label: 'Preheader' },
  { value: '{{subjectLine}}', label: 'Subject line' }
];

const FONT = "font-family:Arial,Helvetica,sans-serif;";

export const PREDEFINED_BLOCKS = [
  {
    type: 'view-in-browser',
    name: 'View in browser',
    html: `<tr>
  <td align="right" style="padding:10px 20px;${FONT}font-size:12px;color:{{brand.mutedTextColor}};">
    <a href="{{brand.viewInBrowserURL}}" target="_blank" style="color:{{brand.mutedTextColor}};text-decoration:underline;">View in browser</a>
  </td>
</tr>`
  },
  {
    type: 'header-logo',
    name: 'Header – Logo',
    html: `<tr>
  <td align="center" style="padding:25px 20px;">
    <a href="{{brand.logoLink}}" target="_blank"><img src="{{brand.logo}}" alt="{{brand.name}}" width="180" style="display:block;border:0;width:180px;max-width:180px;height:auto;"></a>
  </td>
</tr>`
  },
  {
    type: 'header-logo-button',
    name: 'Header – Logo + Button',
    html: `<tr>
  <td style="padding:20px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
      <tr>
        <td align="left" valign="middle">
          <a href="{{brand.logoLink}}" target="_blank"><img src="{{brand.logo}}" alt="{{brand.name}}" width="150" style="display:block;border:0;width:150px;max-width:150px;height:auto;"></a>
        </td>
        <td align="right" valign="middle">
          <a href="{{bodyBtnLink}}" target="_blank" style="display:inline-block;padding:10px 20px;background-color:{{brand.buttonColor}};color:{{brand.buttonTextColor}};border-radius:{{brand.borderRadius}};${FONT}font-size:14px;font-weight:bold;text-decoration:none;">{{bodyBtnName}}</a>
        </td>
      </tr>
    </table>
  </td>
</tr>`
  },
  {
    type: 'hero-image',
    name: 'Hero image',
    html: `<tr>
  <td align="center" style="padding:0;">
    <a href="{{bodyImageLink}}" target="_blank"><img src="{{bodyImage}}" alt="" width="600" style="display:block;border:0;width:100%;max-width:600px;height:auto;"></a>
  </td>
</tr>`
  },
  {
    type: 'headline',
    name: 'Headline',
    html: `<tr>
  <td align="left" style="padding:30px 30px 10px 30px;${FONT}font-size:26px;line-height:32px;font-weight:bold;color:{{brand.textColor}};">
    {{headline}}
  </td>
</tr>`
  },
  {
    type: 'body-text',
    name: 'Body text',
    html: `<tr>
  <td align="left" style="padding:10px 30px;${FONT}font-size:16px;line-height:24px;color:{{brand.textColor}};">
    {{body}}
  </td>
</tr>`
  },
  {
    type: 'button',
    name: 'Button',
    html: `<tr>
  <td align="center" style="padding:20px 30px;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0">
      <tr>
        <td align="center" bgcolor="{{brand.buttonColor}}" style="background-color:{{brand.buttonColor}};border-radius:{{brand.borderRadius}};">
          <a href="{{bodyBtnLink}}" target="_blank" style="display:inline-block;padding:14px 36px;${FONT}font-size:18px;font-weight:bold;color:{{brand.buttonTextColor}};text-decoration:none;border-radius:{{brand.borderRadius}};">{{bodyBtnName}}</a>
        </td>
      </tr>
    </table>
  </td>
</tr>`
  },
  {
    type: 'text-under-button',
    name: 'Text under button',
    html: `<tr>
  <td align="center" style="padding:0 30px 20px 30px;${FONT}font-size:14px;line-height:20px;color:{{brand.mutedTextColor}};">
    {{additionalTextUnderBTN}}
  </td>
</tr>`
  },
  {
    type: 'image-text',
    name: 'Image + Text (2 columns)',
    html: `<tr>
  <td style="padding:20px 30px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
      <tr>
        <td width="50%" valign="top" style="padding-right:10px;">
          <a href="{{bodyImageLink}}" target="_blank"><img src="{{bodyImage}}" alt="" width="260" style="display:block;border:0;width:100%;max-width:260px;height:auto;"></a>
        </td>
        <td width="50%" valign="top" style="padding-left:10px;${FONT}font-size:16px;line-height:24px;color:{{brand.textColor}};">
          {{additionalText}}
        </td>
      </tr>
    </table>
  </td>
</tr>`
  },
  {
    type: 'additional-text',
    name: 'Additional text',
    html: `<tr>
  <td align="left" style="padding:10px 30px;${FONT}font-size:16px;line-height:24px;color:{{brand.textColor}};">
    {{additionalText}}
  </td>
</tr>`
  },
  {
    type: 'divider',
    name: 'Divider',
    html: `<tr>
  <td style="padding:20px 30px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
      <tr>
        <td style="border-top:1px solid #e0e0e0;font-size:0;line-height:0;">&nbsp;</td>
      </tr>
    </table>
  </td>
</tr>`
  },
  {
    type: 'spacer',
    name: 'Spacer',
    html: `<tr>
  <td style="height:30px;font-size:0;line-height:0;">&nbsp;</td>
</tr>`
  },
  {
    type: 'disclaimer',
    name: 'Marketing disclaimer',
    html: `<tr>
  <td align="left" style="padding:20px 30px;${FONT}font-size:12px;line-height:18px;color:{{brand.mutedTextColor}};">
    {{brand.disclaimer}}
  </td>
</tr>`
  },
  {
    type: 'footer',
    name: 'Footer',
    html: `<tr>
  <td align="center" style="padding:20px 30px 30px 30px;${FONT}font-size:11px;line-height:16px;color:{{brand.mutedTextColor}};">
    {{brand.smallDisclaimer}}<br><br>
    <a href="{{brand.unsubscribeURL}}" target="_blank" style="color:{{brand.mutedTextColor}};text-decoration:underline;">Unsubscribe</a>
  </td>
</tr>`
  }
];

// Blocks are inserted into <table> as rows, so they must be one or more complete <tr>...</tr>
// with balanced table tags - anything else breaks the layout in email clients.
// Keep in sync with backend/utils/validate-email-block.js
const TABLE_TAG = /<(\/?)(table|thead|tbody|tfoot|tr|td|th)\b[^>]*>/gi;
const FORBIDDEN_TAG = /<\s*(!doctype|html|head|body|script|style)\b/i;

export const validateBlockHtml = (html) => {
  const code = (html || '').trim();
  if (!code) return 'The block is empty.';
  if (!/^<tr[\s>]/i.test(code)) return 'The block must start with <tr>.';
  if (!/<\/tr>$/i.test(code)) return 'The block must end with </tr>.';
  const forbidden = code.match(FORBIDDEN_TAG);
  if (forbidden) return `<${forbidden[1]}> is not allowed inside a block.`;

  const stack = [];
  let lastIndex = 0;
  let match;
  TABLE_TAG.lastIndex = 0;
  while ((match = TABLE_TAG.exec(code)) !== null) {
    const [, closing, rawTag] = match;
    const tag = rawTag.toLowerCase();

    // Between top-level rows only whitespace is allowed
    if (stack.length === 0) {
      if (code.slice(lastIndex, match.index).trim()) return 'Content outside <tr>...</tr> is not allowed.';
      if (closing || tag !== 'tr') return `Expected <tr> at top level, found <${closing}${tag}>.`;
    }

    if (closing) {
      const open = stack.pop();
      if (open !== tag) return open ? `<${open}> is closed by </${tag}>.` : `Unexpected </${tag}>.`;
    } else {
      stack.push(tag);
    }
    lastIndex = TABLE_TAG.lastIndex;
  }

  if (stack.length > 0) return `<${stack[stack.length - 1]}> is not closed.`;
  if (!/<td[\s>]|<th[\s>]/i.test(code)) return 'A row needs at least one <td>.';
  return null;
};

// Brand border radius is stored as a number of pixels; older values may include a unit
const toPx = (value, fallback = '4px') => {
  if (value === undefined || value === null || String(value).trim() === '') return fallback;
  return /^\d+(\.\d+)?$/.test(String(value).trim()) ? `${String(value).trim()}px` : String(value).trim();
};

// Readable text on the brand background: light text on dark backgrounds and vice versa
const isDarkColor = (color) => {
  const hex = String(color || '').trim().replace('#', '');
  const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return false;
  const [r, g, b] = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16));
  return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
};

const buildPlaceholderValues = (brand = {}, content = {}) => ({
  'brand.name': brand.brandName || '',
  'brand.logo': brand.logo || '',
  'brand.logoLink': brand.logoLink || '#',
  'brand.backgroundColor': brand.backgroundColor || '#ffffff',
  'brand.frameColor': brand.frameColor || '#f4f4f4',
  'brand.textColor': isDarkColor(brand.backgroundColor) ? '#ffffff' : '#222222',
  'brand.mutedTextColor': isDarkColor(brand.backgroundColor) ? '#d0d0d0' : '#777777',
  'brand.buttonColor': brand.buttonGenralColor || '#E66239',
  'brand.buttonTextColor': brand.buttonTextColor || '#ffffff',
  'brand.borderRadius': toPx(brand.borderRadius),
  'brand.viewInBrowserURL': brand.viewInBrowserURL || '#',
  'brand.unsubscribeURL': brand.UNSUBSCRIBE_URL || '#',
  'brand.disclaimer': brand.disclaimer_marketing || '',
  'brand.smallDisclaimer': brand.smalldisclaimer || '',
  headline: content.headline || '',
  body: content.body || '',
  bodyImage: content.bodyImage || '',
  bodyImageLink: content.bodyImageLink || '#',
  bodyBtnName: content.bodyBtnName || '',
  bodyBtnLink: content.bodyBtnLink || '#',
  additionalText: content.additionalText || '',
  additionalTextUnderBTN: content.additionalTextUnderBTN || '',
  preHeader: content.preHeader || '',
  subjectLine: content.subjectLine1 || content.subjectLine || ''
});

export const fillPlaceholders = (html, values) =>
  html.replace(/{{\s*([\w.]+)\s*}}/g, (placeholder, key) => (key in values ? values[key] : placeholder));

// Full email document for a blocks structure
export const renderBlocksEmail = (structure, brand = {}, content = {}) => {
  const values = buildPlaceholderValues(brand, content);
  const rows = (structure?.blocks || []).map(block => fillPlaceholders(block.html || '', values)).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<meta http-equiv="X-UA-Compatible" content="IE=edge">
<title>${values['brand.name']}</title>
<style>
  body { margin:0; padding:0; -webkit-text-size-adjust:100%; -ms-text-size-adjust:100%; }
  img { border:0; outline:none; text-decoration:none; }
  @media only screen and (max-width:620px) {
    .email-container { width:100% !important; }
  }
</style>
</head>
<body style="margin:0;padding:0;background-color:${values['brand.frameColor']};">
<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;">${values.preHeader}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${values['brand.frameColor']};">
  <tr>
    <td align="center" style="padding:20px 10px;">
      <table role="presentation" class="email-container" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;background-color:${values['brand.backgroundColor']};">
${rows}
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
};

// Sample content for previews in the builder and the templates list
export const SAMPLE_CONTENT = {
  headline: 'Your headline goes here',
  body: '<p style="margin:0 0 12px 0;">This is the body text of the creative. It is replaced with the real content when the email is generated.</p>',
  bodyImage: 'https://placehold.co/600x300/e0e0e0/666666?text=Image',
  bodyImageLink: '#',
  bodyBtnName: 'Call to action',
  bodyBtnLink: '#',
  additionalText: 'Additional text from the creative.',
  additionalTextUnderBTN: 'Text shown under the button.',
  preHeader: 'Preheader text',
  subjectLine1: 'Subject line'
};
